//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExpress Imports

import DevExpress from 'devextreme'
import DataSource from 'devextreme/data/data_source'

//#endregion

import { ColumnModel, GridModel, MeterModel, MeterFormValuesModel } from '../../api/api.models'
import { Api, LocalDictionaryFactory } from '../../api/api.module'

import { GridOptions, GridController, GridTypes } from '../../common/grid/grid.controller'
import { i18n } from '../../common/i18n/common.i18n'

import { IUserSession, TypeHelper, LocalizationHelper, ToastHelper } from "../../../helpers"
import { GridService } from '../../../services/grid'

import { Enums, Activity } from '../../../models/common'
import { ConnectedObject } from '../../common/connected-object/connected-object.directive'
import { IMeterAlarmDef, MeterAlarmDefOptions } from './meter-alarm-def.interfaces'

export class MeterAlarmDefinitionsController implements ng.IComponentController, IMeterAlarmDef {

    // #region Consts

    private static get METER_DETAILS_ALARM_DEFS(): string { return 'MeterDetailsAlarmDefs' }

    // #endregion 

    // #region Members

    private units: ColumnModel[]
    private grid: GridController<any>
    private gridOptions: GridOptions
    private options: MeterAlarmDefOptions
    private alarmDefRefFields: ColumnModel[]
    private alarmDefsItems: any[] = []
    private currentAlarmDefs: any[] = []
    private alarmWithTemplates: any[] = []
    private dataGrid: any

    // #endregion 

    // #region Ctor

    constructor(
        private api: Api,
        private $element: JQuery,
        private gridService: GridService,
        private localDictionary: LocalDictionaryFactory,
        private userSession: IUserSession
    ) {
    }

    // #endregion

    // #region ng.IComponentController

    public $onInit() {
    }

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    // #endregion


    // #region Setup

    private setMainGridOptions(): any {
        this.gridOptions = {
            name: MeterAlarmDefinitionsController.METER_DETAILS_ALARM_DEFS,
            type: GridTypes.NO_SEARCH_BAR_SMALL,
            ignoreKey: true,
            noDataText: this.options.meterId === 0 ? LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoAssignedMeter) : null,
            columns: [{
                dataField: "isEnabled",
                caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.IsEnabled),
                dataType: 'boolean'
            }, {
                dataField: "name",
                dataType: "string",
                caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Name),
                selectedFilterOperation: 'contains'
            }, {
                dataField: "value",
                caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Value),
                visible: true,
                selectedFilterOperation: 'contains',
                calculateCellValue: (data: any) => {
                    if (!data.isVisible) {
                        return TypeHelper.emptyString
                    }

                    if (data.systemAlarmValue) {
                        if (data.unit == null) {
                            data.unit = TypeHelper.emptyString
                        }
                        return (typeof (data.systemAlarmValue) == typeof (true) ? LocalizationHelper.getLocalizedText(data.systemAlarmValue.toString()) : data.systemAlarmValue) + ' ' + data.unit
                    }
                    const found: ColumnModel = _.find(this.alarmDefRefFields, (refField: ColumnModel) => {
                        return refField.id == data.idDataTypeConfig
                    })

                    if (found != null) {
                        return found.caption
                    }
                    return TypeHelper.emptyString
                }
            }],
            columnAutoWidth: false,
            buttonsOptions: {
                enabled: false
            },
            masterDetail: {
                enabled: false,
                template: "detail",
                autoExpandAll: true
            },
            dataSource: new DataSource(this.alarmDefsItems),
            addCustomFilters: true,
            allowColumnReordering: !this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.DeviceAlarmDef),
            onDataReady: (e: any) => {
                this.dataGrid.repaint()
            },
            onInitialized: (e: any, grid: GridController<any>) => {
                this.grid = grid
                this.dataGrid = e.component
            },
            onContentReady: (e: any) => {
                this.contentReady()
            }
        }
    }

    private initialize(): void {
        if (this.options.meterId != null && this.options.meterId !== 0) {
            this.getAlarmDefinitions()
                .then((alarmDefinition: GridModel) => {
                    this.alarmWithTemplates = alarmDefinition.values
                    $.when(this.getAlarmReferenceColumns(), this.getUnits())
                        .then((alarmReferenceColumns: ColumnModel[], unitColumns: ColumnModel[]) => {
                            this.alarmDefRefFields = alarmReferenceColumns
                            this.units = unitColumns
                            alarmDefinition.values = this.removeTemplatesFromItems(alarmDefinition.values)
                            alarmDefinition.values.forEach((element: any) => {
                                this.setUnitForDefinitions(element.idDataTypeAlarm)
                                    .then((unit: string) => {
                                        if (!isNaN(element.systemAlarmValue)) {
                                            element.unit = unit
                                        }
                                        if (element.id != 0) {
                                            this.alarmDefsItems.push(element)
                                        }
                                    })
                                this.currentAlarmDefs = this.alarmDefsItems
                            })
                            this.setMainGridOptions()
                        })
                })
        } else {
            this.setMainGridOptions()
        }
        this.onInitialized()
    }

    private setUnitForDefinitions(alarmId: string): JQueryPromise<string> {
        const defer: JQueryDeferred<string> = $.Deferred<string>()
        this.localDictionary
            .getIdUnitByIdDataType(parseInt(alarmId))
            .then((unitId: number) => {
                for (let i = 0; i < this.units.length; i++) {
                    if (this.units[i].id == unitId) {
                        defer.resolve(this.units[i].caption)
                        return
                    }
                }
                defer.resolve(TypeHelper.emptyString)
            })
            .catch(() => {
                defer.reject()
            })
        return defer.promise()
    }

    private getUnits(): JQueryPromise<ColumnModel[]> {
        const defer: JQueryDeferred<ColumnModel[]> = $.Deferred<ColumnModel[]>()

        this.localDictionary
            .getUnits()
            .then((result: ColumnModel[]) => {
                defer.resolve(result)
            })
            .catch(() => {
                defer.reject()
            })

        return defer.promise()
    }

    private getAlarmReferenceColumns(): JQueryPromise<ColumnModel[]> {
        return this.gridService
            .getColumnModelByCategory(
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEF_REF_PARAMS,
                Enums.SimaxModuleName.Meter_Alarm, {
                    groupReferenceType: Enums.ReferenceType.IdDistributor,
                    groupReferenceValue: this.userSession.getLoggedOperator().idDistributor,
                    skipView: true
                })
    }

    private getAlarmDefinitions(): angular.IPromise<GridModel> {
        return this.api.meter.getAlarmDefs(this.options.meterId)
    }

    private removeTemplatesFromItems(values: any[]): any[] {
        const tempArr: any[] = []
        if (values != null && values.length > 0) {
            for (let i = 0; i < values.length; i++) {
                for (let j = 0; j < values.length; j++) {
                    if (values[i].name == values[j].name && values[i].id != 0) {
                        tempArr.push(values[i])
                        break
                    }
                }
            }
        }
        return tempArr
    }

    private addToStringToDefinitions(values: any[]): any[] {
        if (values != null && values.length > 0)
            values.forEach((element: any) => {
                element.toString = () => element.name
            })

        return values
    }

    // #endregion


    // #region Events

    public updateDataSource(values: any): void {
        this.currentAlarmDefs = this.removeTemplatesFromItems(this.addToStringToDefinitions(values))
        this.grid.setGetDataSource(() => this.currentAlarmDefs)
        this.alarmWithTemplates = values
    }

    public getCurrentAlarmDefs(): any {
        return this.grid.dataSource
    }

    private onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }
    private contentReady(): void {
        if (this.options != null && _.isFunction(this.options.onLoaded)) {
            this.options.onLoaded(this.currentAlarmDefs, this.alarmWithTemplates)
        }
    }

    public getConnectedObject(): JQueryPromise<ConnectedObject> {
        const defer: JQueryDeferred<any> = $.Deferred<any>()
        const hasPermission: boolean = !this.userSession.getLoggedOperator().hasPermission(Activity.METER_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.MeterParametersCurrentDevice)

        this.api.meter.getMeter(this.options.meterId)
            .then((response: MeterModel) => {

                const connectedObject: ConnectedObject = {
                    connectedObjectTitle: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CurrentDevice),
                    connectedObjectName: TypeHelper.emptyString,
                    connectedObjectVisibility: false,
                    connectedObjectId: 0,
                    connectedObjectMethod: 'device',
                    connectedObjectAllowLink: this.userSession.getLoggedOperator().hasPermission(Activity.DEVICE_LIST)
                }

                if (response.currentDeviceId != null && hasPermission) {
                    connectedObject.connectedObjectName = response.currentDeviceName
                    connectedObject.connectedObjectVisibility = true
                    connectedObject.connectedObjectId = response.currentDeviceId
                    defer.resolve(connectedObject)
                } else {
                    connectedObject.connectedObjectVisibility = false
                    connectedObject.connectedObjectId = 0
                    connectedObject.connectedObjectName = TypeHelper.emptyString
                    defer.resolve(connectedObject)
                }
            })

        return defer.promise()
    }

    public getAlarmGroup(): JQueryPromise<any> {
        const defer: JQueryDeferred<any> = $.Deferred<any>()

        this.api.meter.getAlarmGroups(this.options.meterId)
            .then((response: GridModel) => {
                defer.resolve(response.values)
            })

        return defer.promise()
    }

    public saveMeter(formModel: MeterFormValuesModel, isDefs: boolean = false): JQueryPromise<MeterModel> {
        const deferred: JQueryDeferred<any> = $.Deferred<any>()

        if (formModel) {
            this.api.meter.saveMeter(formModel).then((response: MeterModel) => {
                if (!response.hasErrorCode) {
                    if (!isDefs)
                        LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Save + 'MeterSaved')
                    if (isDefs)
                        LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Save + 'MeterDefSaved')

                    deferred.resolve(response)
                } else {
                    deferred.reject(response.errorMessage)
                }
            }).catch((err: any) => {
                if (!isDefs)
                    ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Save + 'MeterSaveError'))
                if (isDefs)
                    ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Save + 'MeterDefSaveError'))
                deferred.reject()
            })
        }

        return deferred.promise()
    }

    // #endregion

}